import React, { useState, useRef, useLayoutEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

import { Transition } from 'react-transition-group';
import { SidebarAddButton } from '../SidebarAddButton';
import { Icon, Text } from '../../atoms';

const NavigationHeaderContainer = styled.section`
  display: flex;
  flex-direction: column;
  width: 100%;
  row-gap: 4px;
`;

const StyledSidebarAddButton = styled(SidebarAddButton)`
  margin-left: auto;
  visibility: hidden;
  opacity: 0;
  transition: opacity 100ms linear;
`;

const SectionHeaderText = styled(Text)`
  font-weight: 600;
  color: var(--content-subtitle);
  padding: 8px 0 8px 8px;
  column-gap: 8px;
  display: flex;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  font-family: var(--font-family-title);
  cursor: pointer;

  transition: color 150ms linear;

  &:hover {
    color: var(--content-title);
  }

  &:hover ${StyledSidebarAddButton} {
    visibility: visible;
    opacity: 1;
  }
`;

const TransitionContainer = styled.div`
  display: flex;
  flex-direction: column;

  transition: max-height 200ms linear;
  overflow: hidden;
  max-height: ${({ $state, $height }) =>
    $state === 'entered' || $state === 'entering' ? `${$height}px` : 0};
`;

const AnimatedIcon = styled(Icon)`
  transition: transform 200ms linear;
  transform: ${({ isOpen }) => (isOpen ? 'rotate(0deg)' : 'rotate(-90deg)')};
`;

const NavigationHeader = ({ label, url, children = null, isReadOnly = false }) => {
  const childrenRef = useRef(null);
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(true);
  const [height, setHeight] = useState(0);

  const handleAddClick = (evt) => {
    evt.stopPropagation();
    navigate(url);
  };

  useLayoutEffect(() => {
    if (childrenRef.current) {
      setHeight(childrenRef.current.scrollHeight);
    }
  }, [isOpen, children]);

  return (
    <NavigationHeaderContainer>
      <SectionHeaderText as='span' onClick={() => setIsOpen(!isOpen)}>
        <AnimatedIcon size={16} name='chevron-down' isOpen={isOpen} />
        {label}
        {!isReadOnly && <StyledSidebarAddButton onClick={handleAddClick} />}
      </SectionHeaderText>
      <Transition in={isOpen} timeout={200} mountOnEnter unmountOnExit>
        {(state) => (
          <TransitionContainer ref={childrenRef} $state={state} $height={height}>
            {children}
          </TransitionContainer>
        )}
      </Transition>
    </NavigationHeaderContainer>
  );
};

NavigationHeader.propTypes = {
  label: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  children: PropTypes.node,
  isReadOnly: PropTypes.bool
};

export { NavigationHeader };
